import { createContext, useContext, useState, useCallback } from 'react';

const ConfirmContext = createContext(null);

/**
 * Remplace window.confirm / window.alert / window.prompt par des modals cohérents avec le
 * reste de l'app (les dialogues natifs du navigateur ne peuvent pas être stylés et cassent le
 * design partout où ils apparaissent).
 *
 * const confirm = useConfirm(); if (!(await confirm('Supprimer ?'))) return;
 * const alerte  = useAlerte();  await alerte('Enregistré avec succès.');
 * const prompt  = usePrompt();  const val = await prompt('Résolution :'); if (val) ...
 *
 * Options communes : { titre, danger, confirmLabel, cancelLabel }. `prompt` accepte en plus
 * { valeurInitiale, placeholder }.
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { mode: 'confirm'|'alerte'|'prompt', message, titre, danger, confirmLabel, cancelLabel, valeur, resolve }

  const confirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        mode: 'confirm', message,
        titre:        opts.titre || 'Confirmer l\'action',
        danger:       opts.danger !== false,
        confirmLabel: opts.confirmLabel || 'Confirmer',
        cancelLabel:  opts.cancelLabel || 'Annuler',
        resolve,
      });
    });
  }, []);

  const alerte = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        mode: 'alerte', message,
        titre:        opts.titre || 'Information',
        danger:       opts.danger === true,
        confirmLabel: opts.confirmLabel || 'OK',
        resolve,
      });
    });
  }, []);

  const prompt = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        mode: 'prompt', message,
        titre:        opts.titre || 'Saisie requise',
        confirmLabel: opts.confirmLabel || 'Valider',
        cancelLabel:  opts.cancelLabel || 'Annuler',
        placeholder:  opts.placeholder || '',
        valeur:       opts.valeurInitiale || '',
        resolve,
      });
    });
  }, []);

  /**
   * Confirmer AVEC une case à cocher, pour les décisions qui en portent deux.
   *
   * « Publier ce contenu ? » et « faut-il aussi prévenir par e-mail ? » se posent au même
   * instant : les séparer en deux boîtes de dialogue ferait cliquer deux fois sur ce qui
   * est un seul geste, et la seconde question arriverait après que la première a déjà
   * engagé l'envoi.
   *
   * RÉSOUT UN OBJET `{ ok, coche }`, et non un booléen comme `confirm`. C'est délibérément
   * une fonction distincte : changer le retour de `confirm` casserait silencieusement les
   * dizaines de `if (!(await confirm(…))) return;` du projet — un objet est toujours vrai.
   */
  const confirmAvecOption = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        mode: 'confirm-option', message,
        titre:        opts.titre || "Confirmer l'action",
        danger:       opts.danger === true,
        confirmLabel: opts.confirmLabel || 'Confirmer',
        cancelLabel:  opts.cancelLabel || 'Annuler',
        optionLibelle: opts.optionLibelle || '',
        optionAide:    opts.optionAide || '',
        coche:         opts.optionParDefaut === true,
        resolve,
      });
    });
  }, []);

  function repondre(valeur) {
    state?.resolve(valeur);
    setState(null);
  }

  return (
    <ConfirmContext.Provider value={{ confirm, alerte, prompt, confirmAvecOption }}>
      {children}
      {state && (
        <div
          onClick={() => repondre(state.mode === 'prompt' ? null : state.mode === 'confirm-option' ? { ok: false, coche: false } : false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,35,0.6)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, padding: '26px 28px', maxWidth: 420, width: '90vw', boxShadow: '0 30px 80px rgba(0,0,0,0.3)', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
          >
            <div style={{ fontSize: 16, fontWeight: 800, color: state.danger ? '#991B1B' : '#0B1220', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              {state.danger && <span style={{ fontSize: 18 }}>⚠️</span>}
              {state.titre}
            </div>
            <div style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.6, marginBottom: state.mode === 'prompt' ? 12 : 22, whiteSpace: 'pre-line' }}>
              {state.message}
            </div>
            {state.mode === 'prompt' && (
              <input
                autoFocus
                type="text"
                value={state.valeur}
                placeholder={state.placeholder}
                onChange={e => setState(s => ({ ...s, valeur: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter' && state.valeur.trim()) repondre(state.valeur); if (e.key === 'Escape') repondre(null); }}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 9, border: '1.5px solid #e5e7eb', fontSize: 13, fontFamily: 'inherit', outline: 'none', marginBottom: 22 }}
              />
            )}
            {state.mode === 'confirm-option' && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 18,
                padding: '11px 13px', borderRadius: 10, background: '#F0F9FF', border: '1px solid #BAE6FD', cursor: 'pointer' }}>
                <input type="checkbox" checked={state.coche}
                  onChange={e => setState(s => ({ ...s, coche: e.target.checked }))}
                  style={{ marginTop: 2, width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                <span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0C4A6E' }}>{state.optionLibelle}</span>
                  {state.optionAide && (
                    <span style={{ display: 'block', fontSize: 11.5, color: '#0369A1', lineHeight: 1.5, marginTop: 2 }}>
                      {state.optionAide}
                    </span>
                  )}
                </span>
              </label>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              {state.mode !== 'alerte' && (
                <button
                  onClick={() => repondre(state.mode === 'prompt' ? null : state.mode === 'confirm-option' ? { ok: false, coche: false } : false)}
                  style={{ flex: 1, padding: '10px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {state.cancelLabel}
                </button>
              )}
              <button
                onClick={() => repondre(state.mode === 'prompt' ? state.valeur : state.mode === 'confirm-option' ? { ok: true, coche: !!state.coche } : true)}
                disabled={state.mode === 'prompt' && !state.valeur.trim()}
                autoFocus={state.mode !== 'prompt'}
                style={{ flex: 1, padding: '10px', borderRadius: 9, border: 'none', background: (state.mode === 'prompt' && !state.valeur.trim()) ? '#93c5fd' : (state.danger ? '#dc2626' : '#2563eb'), color: '#fff', fontSize: 13, fontWeight: 700, cursor: (state.mode === 'prompt' && !state.valeur.trim()) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

function useConfirmContext() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm/useAlerte/usePrompt doivent être utilisés sous <ConfirmProvider>');
  return ctx;
}

export function useConfirm() { return useConfirmContext().confirm; }
export function useAlerte()  { return useConfirmContext().alerte; }
export function usePrompt()  { return useConfirmContext().prompt; }
/** Confirmation portant une case à cocher. Rend `{ ok, coche }`. */
export function useConfirmAvecOption() { return useConfirmContext().confirmAvecOption; }
